import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useItemSelection from './useItemSelection';
import { fetchItemsByIds } from '../services/itemsBulk';
import { getUploadRequest } from '../services/uploadRequests';
import { MAX_MOVE_ITEM_IDS } from '../services/subCollections';
import { canManage, canWriteCard } from '../utils/permissions';
import { selectionChangeCardState } from '../utils/changeCardEntry';
import { lockStateFor, recordPolicyFor } from '../utils/recordPolicy';
import { linkedReadOnlyReason } from '../utils/linkedRecords';
import { getItemDisplayValue } from '../utils/items';
import { normalizeId } from '../utils/ids';

/**
 * The items page's multi-selection and "apply to selected" (KPR-1566, moved
 * out of CollectionItemsPage as it stood): which rows may join a bulk write,
 * the selection itself, the items behind it, and the open state of the five
 * bulk surfaces (components/items/ItemsBulkSurfaces renders them from what
 * this returns).
 *
 * The state lives in the PAGE, above both list bodies: `ItemCardsGrid` is
 * re-mounted per group and a collapsed group's grid is unmounted entirely, so
 * selection kept inside it would vanish with a fold. It is keyed by item id and
 * never by row index — the feed re-sorts, re-filters and pages under it.
 *
 * What stays in the page: the refs the `document` keydown listener reads the
 * selection through (`selectionRef`, `selectRef`, `feedRef`) live where that
 * effect lives, registered once, and the page assigns them from this hook's
 * result every render.
 *
 *   const bulk = useItemsBulkState({
 *     collection, collectionId, cardsById,
 *     cardFilter, feedQuery, review, sortField, sortDirection, linkedOn, totalCount,
 *     viewMode, mapOn, boardOn, sortedItems, navigableItems,
 *     resolveElements, resolveLookupValue,
 *     removeItem, refreshItems, refreshCollection,
 *     message, navigate, searchParams, setSearchParams,
 *   });
 *
 * `searchParams` / `setSearchParams` are the page's own (the `?attach=` door
 * reads and drops one parameter of them); `message` is the page's
 * `App.useApp()` message API.
 */
export default function useItemsBulkState({
	collection,
	collectionId,
	cardsById,
	cardFilter,
	feedQuery,
	review,
	sortField,
	sortDirection,
	linkedOn,
	totalCount,
	viewMode,
	mapOn,
	boardOn,
	sortedItems,
	navigableItems,
	resolveElements,
	resolveLookupValue,
	removeItem,
	refreshItems,
	refreshCollection,
	message,
	navigate,
	searchParams,
	setSearchParams,
}) {
	// The feed's generation, said as a string. Any change to these is a
	// change to WHICH items are in the list, so the selection goes with it:
	// an apply to a set the user is no longer looking at is the failure this
	// guards. (`view` and `group` are deliberately absent — they rearrange
	// the same items and a selection survives both.)
	const selectionScope = `${collectionId}|${feedQuery}|${cardFilter}|${review || ''}|${sortField}|${sortDirection}|${linkedOn ? 'linked' : ''}`;
	const selection = useItemSelection({ scopeToken: selectionScope, totalCount });

	// Why a given row cannot take part. Computed from the same two rules the
	// API runs — the card-scoped write grant and the record lock — so a
	// checkbox that is offered is a row the apply will accept, and one that
	// is not says which of the two it is. Validation is all-or-nothing, so a
	// row that would be refused must never join the batch silently.
	const selectionDisabledFor = useCallback((item) => {
		// A linked row never joins a bulk write: nothing here can change it.
		const linkedReason = linkedReadOnlyReason(item);
		if (linkedReason) return linkedReason;
		const cardId = normalizeId(item?.card_id);
		if (collection && !canWriteCard(collection, cardId)) return 'You cannot edit items of this card.';
		const lock = lockStateFor(item, recordPolicyFor(cardsById || {}, cardId));
		if (lock.locked) return lock.reason || 'This record is closed to edits.';
		return null;
	}, [collection, cardsById]);

	// Selection is offered where a bulk write could actually land: not in
	// worklist mode (which has its own submit), and not to a reader who can
	// write nothing in view.
	// Nor on the map (KPR-392): its list is a way to items, not a table to
	// act on in bulk (⚑).
	// Nor on the board (KPR-1395): a card there is moved, not checked.
	const anySelectable = useMemo(
		() => viewMode !== 'worklist' && !mapOn && !boardOn && sortedItems.some(item => !selectionDisabledFor(item)),
		[viewMode, mapOn, boardOn, sortedItems, selectionDisabledFor]
	);
	const selectionEnabled = anySelectable;

	// The order a range runs in is the order ON SCREEN — not the sort, not
	// the fetch order: a collapsed group's items are not between the rows
	// either side of it.
	const orderedIds = useMemo(
		() => navigableItems.map(item => normalizeId(item?._id)),
		[navigableItems]
	);
	const selectableIds = useMemo(
		() => navigableItems.filter(item => !selectionDisabledFor(item)).map(item => normalizeId(item?._id)),
		[navigableItems, selectionDisabledFor]
	);

	// A range runs over the on-screen ORDER but only picks up rows that may
	// actually take part — the same two rules the checkbox itself is disabled
	// by, and the same question shift+arrow already asks before it toggles.
	// Order and membership are separate: the full `orderedIds` is what keeps a
	// collapsed group from being swept in.
	const selectableSet = useMemo(() => new Set(selectableIds), [selectableIds]);
	const canSelectId = useCallback(id => selectableSet.has(id), [selectableSet]);

	const handleToggleSelect = useCallback((id, { selected, shiftKey } = {}) => {
		if (!id) return;
		if (shiftKey) selection.selectRange(id, orderedIds, { canSelect: canSelectId });
		else selection.toggle(id, selected);
	}, [selection, orderedIds, canSelectId]);

	const handleLongPressSelect = useCallback((id) => {
		selection.setSelectionMode(true);
		selection.toggle(id, true);
	}, [selection]);

	// The items behind the selected ids. Ids the feed no longer holds (paged
	// out, or filtered away before the selection was cleared) are re-fetched
	// in chunks, because the panel has to be able to say a locked row's TITLE
	// and not merely its id.
	const [offFeedItems, setOffFeedItems] = useState([]);
	const selectedItems = useMemo(() => {
		const pool = new Map();
		sortedItems.forEach(item => pool.set(normalizeId(item?._id), item));
		offFeedItems.forEach(item => pool.set(normalizeId(item?._id), item));
		return selection.ids.map(id => pool.get(id)).filter(Boolean);
	}, [sortedItems, offFeedItems, selection.ids]);

	// The one card a "set a field" apply needs. Under `allMatching` the FILTER
	// has to name it — there are no ids to read a card off — and a filter that
	// sweeps descendants in names a family, not a card.
	const bulkCardId = useMemo(() => {
		if (selection.allMatching) return cardFilter || null;
		const ids = new Set(selectedItems.map(item => normalizeId(item?.card_id)).filter(Boolean));
		return ids.size === 1 ? Array.from(ids)[0] : null;
	}, [selection.allMatching, cardFilter, selectedItems]);

	// Sub-collections' Move to… goes by the selected ids (never a filter-wide
	// move), 100 at a time.
	const moveDisabled = selection.allMatching || selection.count > MAX_MOVE_ITEM_IDS;
	const moveReason = selection.allMatching
		? 'Select the items to move; a move by filter is not offered.'
		: selection.count > MAX_MOVE_ITEM_IDS ? `Move up to ${MAX_MOVE_ITEM_IDS} items at a time.` : null;

	// Change card (KPR-710): where the selection goes, or why it cannot.
	const changeCard = useMemo(() => {
		const state = selectionChangeCardState({
			collectionId,
			count: selection.count,
			totalCount,
			allMatching: selection.allMatching,
			excludedCount: (selection.excluded || []).length,
			cardId: bulkCardId,
			cardFilter,
			feedQuery,
			ids: selection.ids,
			linkedOn,
			review,
			isManager: canManage(collection),
		});
		return { ...state, go: () => { if (state.path) navigate(state.path); } };
	}, [collectionId, selection.count, selection.allMatching, selection.excluded, selection.ids, totalCount, bulkCardId, cardFilter, feedQuery, linkedOn, review, collection, navigate]);

	const [applyMode, setApplyMode] = useState(null);
	const [moveOpen, setMoveOpen] = useState(false);
	// Tags… on the selection (ItemsTagsPanel): its own panel, since private
	// tags go through their own store.
	const [tagsOpen, setTagsOpen] = useState(false);
	// Attach files… (KPR-187, BulkAttachPanel): the items it acts on, frozen
	// when it opens — picked ids, or a filter (the listed items, or "all N
	// matching" minus the rows taken out of it).
	const [attachScope, setAttachScope] = useState(null);
	// An agent's upload request the panel was opened on (KPR-183), kept apart
	// from the scope: re-reading its counts must not reload the panel's items.
	const [attachRequest, setAttachRequest] = useState(null);
	// Ids already asked for, whatever came back. An id the server does NOT
	// return (it was deleted while the selection was held) would otherwise
	// stay "missing" forever and this effect would refetch it on every
	// render — the fetch is keyed on what was ASKED, never on what arrived.
	const askedForRef = useRef(new Set());
	useEffect(() => {
		if ((!applyMode && !tagsOpen) || selection.allMatching) return undefined;
		const known = new Set(sortedItems.map(item => normalizeId(item?._id)));
		const missing = selection.ids.filter(id => !known.has(id) && !askedForRef.current.has(id));
		if (!missing.length) return undefined;
		missing.forEach(id => askedForRef.current.add(id));
		let live = true;
		fetchItemsByIds(missing)
			.then(rows => { if (live) setOffFeedItems(prev => [...prev, ...rows]); })
			.catch(() => {});
		return () => { live = false; };
	}, [applyMode, tagsOpen, selection.ids, selection.allMatching, sortedItems]);

	const selectionFilter = useMemo(() => ({
		...(cardFilter ? { card_id: cardFilter } : {}),
		...(feedQuery ? { q: feedQuery } : {}),
		// The queue is only nameable by this parameter — the list's default
		// leaves unreviewed submissions out — so a by-filter apply to it has
		// to say so, or it would select the accepted list instead.
		...(review ? { review } : {}),
	}), [cardFilter, feedQuery, review]);

	// Attach files… (KPR-187). Offered only where the collection takes files;
	// the panel itself leaves out every row the selection rules would refuse.
	const takesFiles = collection?.allowAttachments === true;
	const titleForItem = useCallback(item => getItemDisplayValue({
		card: cardsById?.[normalizeId(item?.card_id)],
		elementDefs: resolveElements(item?.card_id),
		item,
		resolveLookupValue,
	}), [cardsById, resolveElements, resolveLookupValue]);
	const canTakeFiles = useCallback(item => !selectionDisabledFor(item), [selectionDisabledFor]);
	const listedScope = useCallback(() => ({
		kind: 'filter', query: feedQuery || '', cardFilter: cardFilter || '', review: review || '', exclude: [],
	}), [feedQuery, cardFilter, review]);
	const openAttachForSelection = useCallback(() => {
		setAttachRequest(null);
		setAttachScope(selection.allMatching
			? { ...listedScope(), exclude: Array.from(selection.excluded || []) }
			: { kind: 'ids', ids: Array.from(selection.ids || []) });
	}, [selection.allMatching, selection.excluded, selection.ids, listedScope]);
	// The page header's ⋯ → Attach files… (CollectionActions) shares no channel
	// with this page, so it says `?attach=1`: open on the items listed, then
	// drop the parameter so a reload does not reopen it.
	const attachParam = searchParams.get('attach');
	// Decided only once the collection is here: on a fresh load (the link
	// opened in a new tab) it arrives after the URL, and reading "takes no
	// files" from a collection not yet loaded would eat the request.
	const collectionLoaded = Boolean(collection?._id);
	// `?attach=<request id>` (KPR-183): an agent's upload request — read with
	// the person's session; the panel opens on its items, pre-filled. A link
	// whose request is for another collection goes there first: this page's
	// grants, cards and locks must be the request's collection's.
	// The route's collection, not only a loaded one: after the hop to another
	// collection the context still holds the old one for a render.
	const collectionHere = collectionLoaded && `${collection._id}` === `${collectionId}`;
	useEffect(() => {
		if (!attachParam || !collectionHere) return;
		if (/^[0-9a-f]{24}$/i.test(attachParam)) {
			getUploadRequest(attachParam)
				.then((request) => {
					if (`${request?.collection_id}` !== `${collection._id}`) {
						navigate(`/collections/${request.collection_id}/items?attach=${request._id}`, { replace: true });
						return;
					}
					if (!takesFiles) {
						message.error('This collection no longer takes files. A manager can turn attachments on in its settings.');
						return;
					}
					const ids = [...new Set((request?.entries || []).map(e => e.itemId))];
					setAttachRequest(request);
					setAttachScope({ kind: 'ids', ids });
				})
				.catch((err) => {
					message.error(err?.response?.status === 404
						? 'This upload link has expired, was withdrawn, or is for someone else.'
						: 'The upload link could not be opened. Try again.');
				});
		} else if (takesFiles) {
			setAttachRequest(null);
			setAttachScope(listedScope());
		}
		setSearchParams(prev => {
			const next = new URLSearchParams(prev);
			next.delete('attach');
			return next;
		}, { replace: true });
	// eslint-disable-next-line react-hooks/exhaustive-deps -- collection._id is read once collectionLoaded
	}, [attachParam, collectionHere, takesFiles, listedScope, setSearchParams, message, navigate]);

	// The attach panel's close and its after-each-file read. Here rather than
	// in ItemsBulkSurfaces so the surfaces reach no service themselves.
	const closeAttach = useCallback(() => { setAttachScope(null); setAttachRequest(null); }, []);
	const rid = attachRequest?._id;
	const handleAttached = useCallback(() => {
		refreshItems();
		// A request's counts move with every file: read it again, so a
		// second pass knows what is already done.
		if (rid) getUploadRequest(rid).then(setAttachRequest).catch(() => {});
	}, [refreshItems, rid]);

	const handleApplied = useCallback((result, { mode, chunks } = {}) => {
		const written = Array.isArray(result?.written) ? result.written : [];
		// A rejected submission is deleted, exactly like a deleted item.
		const deleted = written.filter(row => row.action === 'deleted' || row.action === 'rejected').map(row => row._id);
		if (deleted.length) deleted.forEach(id => removeItem(id));
		if (result?.partial) {
			message.warning(`${written.length} of ${selection.count} changed before this stopped. Nothing else was written.`);
		} else if (chunks > 1) {
			// Chunking breaks all-or-nothing ACROSS chunks, and a success
			// message that does not say so is a promise the client cannot keep.
			message.success(`${written.length} items changed, in ${chunks} batches.`);
		} else {
			message.success({
				delete: `${written.length} items deleted.`,
				accept: `${written.length} submissions accepted.`,
				reject: `${written.length} submissions rejected. They can be restored from History.`,
			}[mode] || `${written.length} items changed.`);
		}
		// The home's "N need review" and this page's own count read the
		// collection record; a triage changed what it says.
		if (mode === 'accept' || mode === 'reject') refreshCollection?.();
		selection.clear();
		setOffFeedItems([]);
		askedForRef.current = new Set();
		setApplyMode(null);
		refreshItems();
	}, [message, selection, removeItem, refreshItems, refreshCollection]);

	return {
		selection,
		selectionEnabled,
		selectionDisabledFor,
		selectableIds,
		handleToggleSelect,
		handleLongPressSelect,
		selectedItems,
		bulkCardId,
		moveDisabled,
		moveReason,
		changeCard,
		selectionFilter,
		applyMode,
		setApplyMode,
		moveOpen,
		setMoveOpen,
		tagsOpen,
		setTagsOpen,
		attachScope,
		attachRequest,
		openAttachForSelection,
		closeAttach,
		handleAttached,
		takesFiles,
		titleForItem,
		canTakeFiles,
		handleApplied,
	};
}
